// Modelos 3D: Sensores. Las medidas siguen las piezas reales (mm) y las constantes de
// client/parts/sensors.js, así los pines de la definición coinciden con las patas del modelo.
import {
  Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, PITCH, THREE,
  sgeo, sharedTex, sharedMat, silkText, pad, lead, meshTexture, fresnelTexture, trimmer3362, soic, smd, COLORS,
} from './sensors-kit.js';
import { HCSR04, DHT, PIR, TO92 } from '../../parts/sensors.js';
import { MODULE_MODELS } from './sensors-modules.js';

/** Malla con textura en un plano vertical que mira hacia +Z (calcomanías de frente). */
function facePlane(w, h, tex, { transparent = false, roughness = 0.5 } = {}) {
  const m = new THREE.MeshStandardMaterial({ map: tex, roughness, transparent, alphaTest: transparent ? 0 : 0.5, depthWrite: !transparent });
  const p = new THREE.Mesh(new THREE.PlaneGeometry(w, h), m);
  p.receiveShadow = true;
  return p;
}

// HC-SR04 ---------------------------------------------------------------------------------------------
const HC_HOLES = [[-20.6, -7.9], [20.6, -7.9], [-20.6, 7.9], [20.6, 7.9]]; // (x, z del PCB acostado)

function hcsr04Front() {
  const G = HCSR04;
  return sharedTex('hcsr04-front', () => {
    const { c, g } = canvas(G.w, G.h, 22);
    const X = (x) => x + G.w / 2;
    const Y = (y) => G.pcbY + G.h - y; // altura en el mundo → renglón del lienzo
    g.fillStyle = COLORS.pcbBlue;
    g.fillRect(0, 0, G.w, G.h);
    // Pistas
    g.strokeStyle = 'rgba(70,120,205,0.55)';
    g.lineWidth = 0.32;
    for (const [x0, y0, x1, y1] of [[-3.81, 3, -6, 6.5], [3.81, 3, 6, 6.5], [-1.27, 3, -2.5, 8], [1.27, 3, 2.5, 8], [-6, 6.5, -9, 6.5], [6, 6.5, 9, 6.5]]) {
      g.beginPath();
      g.moveTo(X(x0), Y(y0));
      g.lineTo(X(x1), Y(y1));
      g.stroke();
    }
    // Pads de los transductores y del cristal
    for (const s of [-1, 1]) for (const d of [-2.2, 2.2]) pad(g, X(s * G.tx + d), Y(G.cy - 6.9), 0.55, 0.3);
    silkText(g, 'HC-SR04', X(0), Y(G.cy + 1.6), 1.85);
    silkText(g, 'T', X(-3.3), Y(G.cy - 2.6), 1.5);
    silkText(g, 'R', X(3.3), Y(G.cy - 2.6), 1.5);
    ['Vcc', 'Trig', 'Echo', 'Gnd'].forEach((l, i) => {
      const x = (i - 1.5) * PITCH;
      silkText(g, l, X(x), Y(G.pinY + 2.0), 0.82);
      pad(g, X(x), Y(G.pinY), 0.78, 0.38);
    });
    g.strokeStyle = COLORS.silk;
    g.lineWidth = 0.15;
    g.strokeRect(X(-5.4), Y(G.pinY + 3.1), 10.8, 3.5);
    for (const [hx, hz] of HC_HOLES) {
      const y = G.cy - hz;
      g.fillStyle = COLORS.pad;
      g.beginPath();
      g.arc(X(hx), Y(y), 1.55, 0, Math.PI * 2);
      g.fill();
    }
    g.globalCompositeOperation = 'destination-out';
    for (const [hx, hz] of HC_HOLES) {
      g.beginPath();
      g.arc(X(hx), Y(G.cy - hz), 1.0, 0, Math.PI * 2);
      g.fill();
    }
    return texture(c);
  });
}

function obstacleLabel(cm) {
  const { c, g } = canvas(36, 9, 14);
  g.fillStyle = 'rgba(10,22,34,0.72)';
  g.beginPath();
  g.roundRect(0.5, 0.5, 35, 8, 2.5);
  g.fill();
  text(g, `obstáculo · ${Math.round(cm * 10) / 10} cm`, 18, 4.7, { size: 4.2, color: '#d6f1ff', weight: 700 });
  return texture(c);
}

export function buildHcsr04(def, props) {
  const G = HCSR04;
  const b = new Builder();
  // PCB vertical: se construye acostado y se gira 90° para que la cara de componentes mire a +Z.
  const pcb = extrudeBoard(boardShape(G.w, G.h, { r: 0.8 }), G.t, null, COLORS.pcbBlue, { holes: HC_HOLES.map(([x, z]) => [x, z, 1.0]), w: G.w, d: G.h, y0: G.back });
  pcb.rotation.x = Math.PI / 2;
  pcb.position.y = G.cy;
  b.root.add(pcb);
  const front = facePlane(G.w, G.h, hcsr04Front());
  front.position.set(0, G.cy, G.front + 0.02);
  b.root.add(front);

  // Transductores: lata de aluminio, borde y malla al fondo de un pequeño hueco.
  const alu = mat('#c6cad0', { roughness: 0.34, metalness: 0.85 });
  const recess = mat('#1d1f22', { roughness: 0.85, side: THREE.DoubleSide });
  const meshMat = sharedMat('hc-mesh', () => new THREE.MeshStandardMaterial({ map: meshTexture('#1c1d20', '#6d7279'), roughness: 0.7, metalness: 0.4 }));
  const zEnd = G.front + G.len;
  for (const s of [-1, 1]) {
    const x = s * G.tx;
    b.cylZ(G.r, G.len - 0.3, alu, x, G.cy, G.front + (G.len - 0.3) / 2, 44);
    b.add(sgeo('hc-rim', () => new THREE.RingGeometry(6.7, G.r, 44)), M.metal, { x, y: G.cy, z: zEnd - 0.29 });
    b.add(sgeo('hc-recess', () => new THREE.CylinderGeometry(6.7, 6.7, 0.9, 44, 1, true)), recess, { x, y: G.cy, z: zEnd - 0.75, rx: Math.PI / 2 });
    b.add(sgeo('hc-mesh', () => new THREE.CircleGeometry(6.7, 44)), meshMat, { x, y: G.cy, z: zEnd - 1.2 });
  }
  // Cristal de 12 MHz (HC-49S) arriba al centro
  b.rbox(10.6, 3.6, 3.3, 1.3, mat('#aeb2b8', { roughness: 0.5, metalness: 0.75 }), 0, G.pcbY + G.h - 4.3, G.front + 1.65);
  // Conector en ángulo recto: plástico detrás del PCB, pines hacia abajo y extremos soldados al frente.
  b.box(4 * PITCH, 2.5, 2.5, M.blackPlastic, 0, G.pinY - 1.25, 0);
  for (let i = 0; i < 4; i++) {
    const x = (i - 1.5) * PITCH;
    b.box(0.64, G.pinY + 0.32, 0.64, M.gold, x, 0, 0);
    b.box(0.64, 0.64, G.front + 0.5, M.gold, x, G.pinY - 0.32, (G.front + 0.5) / 2);
    b.add(sgeo('hc-solder', () => new THREE.CylinderGeometry(0.35, 0.95, 0.6, 12)), M.tin, { x, y: G.pinY, z: G.front + 0.3, rx: Math.PI / 2 });
  }
  // Atrás: los tres circuitos integrados del módulo y componentes pasivos
  for (const [x, y, w, h] of [[-11, 9.5, 5, 6.2], [0, 10.5, 9.8, 4], [11, 9.5, 5, 6.2]]) b.box(w, h, 1.5, M.chip, x, y, G.back - 0.75);
  for (const [x, y] of [[-6, 6.5], [6, 6.5], [-5.5, 15], [5.5, 15], [-16, 15.5], [16, 15.5]]) b.box(2, 1.25, 0.6, M.black, x, y, G.back - 0.3);
  for (const s of [-1, 1]) for (const d of [-2.2, 2.2]) b.add(sgeo('hc-solder', () => new THREE.CylinderGeometry(0.35, 0.95, 0.6, 12)), M.tin, { x: s * G.tx + d, y: G.cy - 6.9, z: G.back - 0.3, rx: -Math.PI / 2 });
  b.build();

  // Pulso ultrasónico: tres frentes de onda que salen del transmisor (T) en cada medición.
  const pings = [0, 1, 2].map(() => {
    const m = new THREE.MeshBasicMaterial({ color: '#9be6ff', transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide });
    const ring = new THREE.Mesh(sgeo('hc-ping', () => new THREE.RingGeometry(0.9, 1, 48)), m);
    ring.userData.noPick = true;
    ring.visible = false;
    b.root.add(ring);
    return ring;
  });

  // Obstáculo translúcido a la distancia ajustada (solo con la simulación corriendo y hasta 50 cm).
  const obstacle = new THREE.Group();
  const wallMat = new THREE.MeshStandardMaterial({ color: '#dbe6ef', transparent: true, opacity: 0.2, depthWrite: false, side: THREE.DoubleSide, roughness: 0.9 });
  const wallGeo = new THREE.PlaneGeometry(54, 40);
  const wall = new THREE.Mesh(wallGeo, wallMat);
  const edge = new THREE.LineSegments(new THREE.EdgesGeometry(wallGeo), new THREE.LineBasicMaterial({ color: '#bfe6ff', transparent: true, opacity: 0.7 }));
  const labelMat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false });
  const label = new THREE.Mesh(new THREE.PlaneGeometry(28.8, 7.2), labelMat);
  label.position.set(0, 20 + 5, 0);
  obstacle.add(wall, edge, label);
  obstacle.position.y = -8.5 + 20; // la base queda a la altura de la mesa si el sensor está en la protoboard
  obstacle.traverse((o) => {
    o.userData.noPick = true;
  });
  let shownCm = null;

  let lastPings = null;
  let t = 1;
  const showObstacle = (v) => {
    const visible = !!(v && v.on && !v.noEcho && v.d <= 50);
    if (visible) {
      obstacle.position.z = zEnd + v.d * 10;
      if (shownCm !== v.d) {
        shownCm = v.d;
        if (labelMat.map) labelMat.map.dispose();
        labelMat.map = obstacleLabel(v.d);
        labelMat.needsUpdate = true;
      }
      if (!obstacle.parent) b.root.add(obstacle);
    } else if (obstacle.parent) {
      b.root.remove(obstacle);
    }
  };
  return {
    root: b.root,
    update(v) {
      showObstacle(v);
      if (lastPings !== null && v.pings !== lastPings) t = 0;
      lastPings = v.pings;
    },
    animate(dt) {
      if (t >= 1) {
        for (const p of pings) p.visible = false;
        return false;
      }
      t = Math.min(1, t + dt / 0.45);
      pings.forEach((p, i) => {
        const k = (t - i * 0.18) / 0.64; // cada frente sale un poco después del anterior
        p.visible = k > 0 && k < 1;
        if (!p.visible) return;
        p.position.set(-G.tx, G.cy, zEnd + 0.5 + k * 26);
        const r = 6.5 + k * 8;
        p.scale.set(r, r, 1);
        p.material.opacity = 0.7 * (1 - k);
      });
      return true;
    },
    reset() {
      lastPings = null;
      t = 1;
      for (const p of pings) p.visible = false;
      showObstacle(null);
    },
    dispose() {
      // El obstáculo puede estar fuera de la escena: se libera aparte.
      if (labelMat.map) labelMat.map.dispose();
      for (const o of [wall, edge, label]) {
        o.geometry.dispose();
        o.material.dispose();
      }
    },
  };
}

// DHT22 / DHT11 ---------------------------------------------------------------------------------------
function dhtFront(type) {
  const D = DHT[type];
  return sharedTex('dht-front-' + type, () => {
    const { c, g } = canvas(D.w, D.h, 24);
    const dark = type === 'dht22' ? '#3b3d40' : '#16243a';
    g.fillStyle = D.color;
    g.fillRect(0, 0, D.w, D.h);
    const cols = 4;
    const rows = type === 'dht22' ? 6 : 5;
    const top = type === 'dht22' ? 7.4 : 2.0;
    const bottom = type === 'dht22' ? 2.2 : 1.6;
    const side = type === 'dht22' ? 1.6 : 1.3;
    const cw = (D.w - 2 * side) / cols;
    const rh = (D.h - top - bottom) / rows;
    const rib = type === 'dht22' ? 0.9 : 0.75;
    for (let r = 0; r < rows; r++) {
      for (let k = 0; k < cols; k++) {
        const x = side + k * cw + rib / 2;
        const y = top + r * rh + rib / 2;
        const w = cw - rib;
        const h = rh - rib;
        g.fillStyle = dark;
        g.fillRect(x, y, w, h);
        // Pared interior iluminada desde arriba: da profundidad a cada ventana
        const grad = g.createLinearGradient(0, y, 0, y + h);
        grad.addColorStop(0, 'rgba(255,255,255,0.28)');
        grad.addColorStop(0.35, 'rgba(255,255,255,0.0)');
        g.fillStyle = grad;
        g.fillRect(x, y, w, h);
        g.fillStyle = 'rgba(0,0,0,0.25)';
        g.fillRect(x, y + h - 0.25, w, 0.25);
      }
    }
    if (type === 'dht22') {
      // Oreja con agujero de montaje
      g.fillStyle = '#2e3033';
      g.beginPath();
      g.arc(D.w / 2, 3.3, 1.55, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = 'rgba(0,0,0,0.12)';
      g.lineWidth = 0.25;
      g.strokeRect(side - 0.2, top - 0.3, D.w - 2 * side + 0.4, D.h - top - bottom + 0.6);
    }
    return texture(c);
  });
}

function dhtBack(type) {
  const D = DHT[type];
  return sharedTex('dht-back-' + type, () => {
    const { c, g } = canvas(D.w, D.h, 20);
    g.fillStyle = D.color;
    g.fillRect(0, 0, D.w, D.h);
    if (type === 'dht22') {
      g.fillStyle = '#2e3033';
      g.beginPath();
      g.arc(D.w / 2, 3.3, 1.55, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = '#e8e6df';
      g.fillRect(1.5, 9, D.w - 3, 9);
      text(g, 'AM2302', D.w / 2, 12, { size: 1.9, color: '#4a4c50', weight: 700 });
      text(g, 'DHT22', D.w / 2, 15.2, { size: 1.6, color: '#6a6c70', weight: 600 });
    } else {
      text(g, 'DHT11', D.w / 2, D.h / 2, { size: 1.8, color: 'rgba(255,255,255,0.55)', weight: 700 });
    }
    return texture(c);
  });
}

export function buildDht(def) {
  const D = DHT[def.type] || DHT.dht22;
  const b = new Builder();
  b.rbox(D.w, D.h, D.d, 0.6, mat(D.color, { roughness: 0.55 }), 0, D.y0, 0);
  for (let i = 0; i < 4; i++) b.box(0.55, D.y0 + 0.5, 0.28, M.tin, (i - 1.5) * PITCH, 0, 0);
  b.build();
  const front = facePlane(D.w - 0.5, D.h - 0.5, dhtFront(def.type), { roughness: 0.55 });
  front.position.set(0, D.y0 + D.h / 2, D.d / 2 + 0.02);
  const back = facePlane(D.w - 0.5, D.h - 0.5, dhtBack(def.type), { roughness: 0.55 });
  back.position.set(0, D.y0 + D.h / 2, -D.d / 2 - 0.02);
  back.rotation.y = Math.PI;
  b.root.add(front, back);
  return { root: b.root };
}

// PIR HC-SR501 ----------------------------------------------------------------------------------------
function pirSilk() {
  return sharedTex('pir-top', () => {
    const { c, g } = canvas(PIR.w, PIR.d, 18);
    g.fillStyle = COLORS.pcbGreen;
    g.fillRect(0, 0, PIR.w, PIR.d);
    const X = (x) => x + PIR.w / 2;
    const Y = (z) => z - PIR.zc + PIR.d / 2;
    for (const x of [-PITCH, 0, PITCH]) pad(g, X(x), Y(0), 0.75, 0.35);
    silkText(g, 'HC-SR501', X(14.6), Y(-10), 1.0, { rotate: -Math.PI / 2 });
    for (const s of [-1, 1]) {
      g.fillStyle = COLORS.pad;
      g.beginPath();
      g.arc(X(s * 14.2), Y(PIR.zc), 1.8, 0, Math.PI * 2);
      g.fill();
    }
    g.globalCompositeOperation = 'destination-out';
    for (const s of [-1, 1]) {
      g.beginPath();
      g.arc(X(s * 14.2), Y(PIR.zc), 1.0, 0, Math.PI * 2);
      g.fill();
    }
    return texture(c);
  });
}

export function buildPir(def, props) {
  const b = new Builder();
  const P = PIR;
  const pcb = extrudeBoard(boardShape(P.w, P.d, { r: 0.6 }), P.t, null, COLORS.pcbGreen, { holes: [[-14.2, 0, 1.0], [14.2, 0, 1.0]], w: P.w, d: P.d, y0: P.pcbY });
  pcb.position.z = P.zc;
  b.root.add(pcb);
  const silk = new THREE.Mesh(new THREE.PlaneGeometry(P.w, P.d).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: pirSilk(), alphaTest: 0.5, roughness: 0.5 }));
  silk.position.set(0, P.top + 0.02, P.zc);
  b.root.add(silk);
  // Marco cuadrado de la lente
  const lensMat = mat('#f1f0ea', { roughness: 0.6 });
  b.rbox(P.lens, 2.6, P.lens, 0.8, lensMat, 0, P.top, P.zc);
  // Conector de 3 pines por abajo (las patas van a la protoboard)
  b.box(3 * PITCH, 2.5, PITCH, M.blackPlastic, 0, P.pcbY - 2.5, 0);
  for (const x of [-PITCH, 0, PITCH]) b.box(0.64, P.top + 0.6, 0.64, M.gold, x, 0, 0);
  // Componentes del lado de abajo: perillas Sx/Tx, puente H/L, BISS0001 y regulador
  const orange = '#f0a01c';
  trimmer3362(b, 5.6, P.pcbY, -4.7, { color: orange, rotor: '#f3efe2', under: true, angle: 0.4 });
  trimmer3362(b, 12.6, P.pcbY, -4.7, { color: orange, rotor: '#f3efe2', under: true, angle: -0.5 });
  soic(b, -1.5, P.pcbY, -13, 16, { under: true });
  b.box(4.5, 1.5, 2.5, M.chip, -9, P.pcbY - 1.5, -15);
  b.box(3.5, 1.2, 1.6, M.chip, 9, P.pcbY - 1.2, -16);
  for (const [x, z] of [[4, -9], [-6, -9], [2, -18], [-12, -10], [11, -11]]) smd(b, x, P.pcbY, z, { under: true, kind: x > 0 ? 'c' : 'r' });
  // Puente de modo: 3 pines (L, centro, H) con la tapa amarilla
  const jx = -11.5;
  const jz = -3.0;
  b.box(3 * PITCH, 2.5, PITCH, M.blackPlastic, jx, P.pcbY - 2.5, jz);
  for (const dx of [-PITCH, 0, PITCH]) b.box(0.64, 4.2, 0.64, M.gold, jx + dx, P.pcbY - 4.2, jz);
  b.build();
  const cap = new THREE.Mesh(sgeo('pir-jumper', () => new THREE.BoxGeometry(2 * PITCH, 4.4, 2.5)), mat('#f2cf2b', { roughness: 0.5 }));
  cap.position.set(jx, P.pcbY - 2.5 - 2.2, jz);
  cap.castShadow = true;
  b.root.add(cap);

  // Cúpula Fresnel (se presiona para simular movimiento)
  const domeMat = new THREE.MeshStandardMaterial({ color: '#ffffff', map: fresnelTexture(), roughness: 0.58, emissive: '#ff6a3d', emissiveIntensity: 0 });
  const dome = new THREE.Mesh(sgeo('pir-dome', () => new THREE.SphereGeometry(P.dome, 48, 20, 0, Math.PI * 2, 0, Math.PI / 2)), domeMat);
  dome.position.set(0, P.top + 2.6, P.zc);
  dome.castShadow = true;
  dome.userData.interactive = { kind: 'press', key: 'motion', label: 'Moverse frente al sensor (mantener)' };
  b.root.add(dome);
  // Ondas alrededor de la cúpula mientras hay movimiento
  const waveMat = new THREE.MeshBasicMaterial({ color: '#ffb36b', transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide });
  const wave = new THREE.Mesh(sgeo('pir-wave', () => new THREE.RingGeometry(0.92, 1, 56).rotateX(-Math.PI / 2)), waveMat);
  wave.position.set(0, P.top + 3, P.zc);
  wave.userData.noPick = true;
  wave.visible = false;
  b.root.add(wave);

  const setMode = (mode) => {
    cap.position.x = jx + (mode === 'single' ? -PITCH / 2 : PITCH / 2);
  };
  setMode(props.mode);
  let motion = false;
  let pressed = false;
  let t = 0;
  const applyPress = () => {
    const on = motion || pressed;
    dome.scale.setScalar(on ? 0.97 : 1);
    domeMat.emissiveIntensity = on ? 0.08 : 0;
  };
  return {
    root: b.root,
    setProps(p) {
      setMode(p.mode);
    },
    update(v) {
      motion = !!v.motion;
      applyPress();
    },
    press(key, down) {
      pressed = down;
      applyPress();
    },
    animate(dt) {
      const on = motion || pressed;
      if (!on && !wave.visible) return false;
      t += dt / 0.9;
      if (t >= 1) {
        t = 0;
        if (!on) {
          wave.visible = false;
          return false;
        }
      }
      wave.visible = true;
      const r = P.dome + 1 + t * 16;
      wave.scale.set(r, 1, r);
      waveMat.opacity = 0.6 * (1 - t);
      return true;
    },
    reset() {
      motion = false;
      pressed = false;
      wave.visible = false;
      applyPress();
    },
  };
}

// Fotorresistencia y termistor ----------------------------------------------------------------------
function ldrTop() {
  return sharedTex('ldr-top', () => {
    const c = document.createElement('canvas');
    c.width = c.height = 256;
    const g = c.getContext('2d');
    g.fillStyle = '#ece4d2';
    g.fillRect(0, 0, 256, 256);
    // Electrodos (gris metálico) y la pista serpenteante de CdS (rojo anaranjado) entre ellos
    g.fillStyle = '#a3a19b';
    g.fillRect(28, 40, 200, 30);
    g.fillRect(28, 186, 200, 30);
    g.strokeStyle = '#c4462a';
    g.lineWidth = 10;
    g.lineJoin = 'round';
    g.beginPath();
    let y = 72;
    g.moveTo(40, y);
    for (let i = 0; i < 7; i++) {
      const x = i % 2 ? 40 : 216;
      g.lineTo(x, y);
      y += 16;
      g.lineTo(x, y);
    }
    g.lineTo(216, y);
    g.stroke();
    // Brillo del recubrimiento de epoxi
    const grad = g.createRadialGradient(100, 90, 10, 128, 128, 140);
    grad.addColorStop(0, 'rgba(255,255,255,0.35)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 256, 256);
    return texture(c);
  });
}

export function buildPhotoresistor() {
  const b = new Builder();
  const y0 = 4.8;
  for (const s of [-1, 1]) lead(b, [[s * 0.95, y0 + 0.3, 0], [s * 0.95, 3.4, 0], [s * PITCH / 2, 2.4, 0], [s * PITCH / 2, 0, 0]], 0.22);
  b.build();
  const side = mat('#d8cfb9', { roughness: 0.22 });
  const top = sharedMat('ldr-top', () => new THREE.MeshStandardMaterial({ map: ldrTop(), roughness: 0.18 }));
  const disc = new THREE.Mesh(sgeo('ldr-disc', () => new THREE.CylinderGeometry(2.55, 2.55, 1.9, 32)), [side, top, side]);
  disc.position.y = y0 + 0.95;
  disc.castShadow = true;
  b.root.add(disc);
  return { root: b.root };
}

/** Termistor MF52: gota de epoxi negra sobre dos patas que se abren al paso de la protoboard. */
export function buildNtc() {
  const b = new Builder();
  const y = 6.6;
  b.add(sgeo('ntc-bead', () => new THREE.SphereGeometry(1.5, 20, 14)), mat('#121214', { roughness: 0.2 }), { y, sx: 1, sy: 1.35, sz: 0.82 });
  for (const s of [-1, 1]) lead(b, [[s * 0.45, y - 1.2, 0], [s * 0.45, y - 2.4, 0], [s * PITCH / 2, 1.6, 0], [s * PITCH / 2, 0, 0]], 0.2);
  b.build();
  return { root: b.root };
}

// Encapsulado TO-92 (LM35, TMP36, DS18B20) ------------------------------------------------------------
function to92Geo() {
  return sgeo('to92-body', () => {
    const R = TO92.r;
    const f = TO92.flat - TO92.zc; // distancia del centro a la cara plana
    const half = Math.sqrt(R * R - f * f);
    const a = Math.atan2(f, half);
    const s = new THREE.Shape();
    s.moveTo(-half, f);
    s.lineTo(half, f);
    s.absarc(0, 0, R, a, Math.PI - a, true);
    const geo = new THREE.ExtrudeGeometry(s, { depth: TO92.h, bevelEnabled: true, bevelThickness: 0.15, bevelSize: 0.12, bevelSegments: 2, curveSegments: 20 });
    // Forma en (x, y) con la cara plana en +y; se extruye hacia arriba y se gira para que mire a +Z.
    geo.rotateX(-Math.PI / 2);
    geo.rotateY(Math.PI);
    geo.translate(0, TO92.y0, TO92.zc);
    return geo;
  });
}

function to92Label(lines) {
  return sharedTex('to92-' + lines.join('|'), () => {
    const { c, g } = canvas(3.6, 4.4, 60);
    lines.forEach((l, i) => text(g, l, 1.8, 0.95 + i * 1.0, { size: 0.74, color: '#9d9fa3', weight: 600 }));
    return texture(c);
  });
}

/** Cuerpo TO-92 con su rotulación; las patas salen a 1.27 mm y se abren a 2.54 mm. */
function buildTo92(lines) {
  const b = new Builder();
  for (const [i, x] of [-PITCH, 0, PITCH].entries()) {
    const top = (i - 1) * 1.27;
    lead(b, [[top, TO92.y0 + 0.3, 0], [top, TO92.y0 - 1.0, 0], [x, 2.2, 0], [x, 0, 0]], 0.21);
  }
  b.build();
  const body = new THREE.Mesh(to92Geo(), mat('#17181a', { roughness: 0.42 }));
  body.castShadow = true;
  b.root.add(body);
  const label = facePlane(3.6, 4.4, to92Label(lines), { transparent: true });
  label.position.set(0, TO92.y0 + TO92.h / 2 + 0.1, TO92.flat + 0.17);
  b.root.add(label);
  return b;
}

export function buildLm35() {
  return { root: buildTo92(['LM35', 'DZ', '2204']).root };
}

export function buildTmp36() {
  return { root: buildTo92(['TMP', '36GZ', '#2215']).root };
}

export function buildDs18b20(def, props) {
  const b = buildTo92(['DALLAS', '18B20', '2219C4']);
  // Resistencia pull-up de 4.7 kΩ entre DQ y VDD, por detrás de las patas
  const res = new THREE.Group();
  const body = new THREE.Mesh(sgeo('ds-res', () => new THREE.CylinderGeometry(0.55, 0.55, 1.8, 14).rotateZ(Math.PI / 2)), mat('#d8c08c', { roughness: 0.5 }));
  body.position.set(PITCH / 2, 1.5, -1.0);
  res.add(body);
  [['#e8c21c', -0.55], ['#7b3fb5', -0.15], ['#d4302a', 0.25], ['#c9a33c', 0.6]].forEach(([color, dx]) => {
    const band = new THREE.Mesh(sgeo('ds-band', () => new THREE.CylinderGeometry(0.57, 0.57, 0.18, 14).rotateZ(Math.PI / 2)), mat(color, { roughness: 0.5 }));
    band.position.set(PITCH / 2 + dx, 1.5, -1.0);
    res.add(band);
  });
  for (const x of [0, PITCH]) {
    const wire = new THREE.Mesh(sgeo('ds-res-lead', () => new THREE.CylinderGeometry(0.13, 0.13, 1.25, 6).rotateX(Math.PI / 2)), M.tin);
    wire.position.set(x + (x ? -0.25 : 0.25), 1.5, -0.45);
    res.add(wire);
  }
  b.root.add(res);
  const set = (p) => {
    res.visible = !!p.pullup;
  };
  set(props);
  return { root: b.root, setProps: set };
}

// Interruptor de inclinación SW-520D -----------------------------------------------------------------
export function buildTiltSwitch(def, props) {
  const b = new Builder();
  const base = 2.6;
  for (const s of [-1, 1]) lead(b, [[s * 0.9, base, 0], [s * PITCH / 2, base - 0.9, 0], [s * PITCH / 2, 0, 0]], 0.22);
  b.build();
  const pivot = new THREE.Group();
  pivot.position.y = base;
  b.root.add(pivot);
  const can = new Builder();
  can.cyl(2.85, 2.85, 10.6, mat('#c7c9cc', { roughness: 0.3, metalness: 0.9 }), 0, 0.6, 0, 32);
  can.cyl(2.6, 2.85, 0.5, mat('#c7c9cc', { roughness: 0.3, metalness: 0.9 }), 0, 11.2, 0, 32);
  can.cyl(2.75, 2.75, 0.6, mat('#1e1f22', { roughness: 0.6 }), 0, 0, 0, 32);
  can.build();
  can.root.traverse((o) => {
    if (o.isMesh) o.userData.interactive = { kind: 'toggle', key: 'tilted', label: 'Inclinar o enderezar' };
  });
  pivot.add(can.root);
  const set = (p) => {
    pivot.rotation.z = p.tilted ? -0.62 : 0;
  };
  set(props);
  return { root: b.root, setProps: set };
}

export const MODELS = {
  'hc-sr04': buildHcsr04,
  dht22: buildDht,
  dht11: buildDht,
  pir: buildPir,
  photoresistor: buildPhotoresistor,
  ntc: buildNtc,
  lm35: buildLm35,
  tmp36: buildTmp36,
  ds18b20: buildDs18b20,
  'tilt-switch': buildTiltSwitch,
  ...MODULE_MODELS,
};
